import type { Object3D } from 'three';
import type {
	SketchEntityReferencePreviewDisposableResource,
	SketchEntityReferencePreviewRetirementServiceDependencies,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewRetirementService';
import { scheduleDisposableResourceDisposal } from '../../../../utils/scheduleDisposableResourceDisposal';
import { clearSketchEntityReferencePreview } from './SketchEntityReferencePreviewMarker';

export type {
	SketchEntityReferencePreviewDisposableResource,
	SketchEntityReferencePreviewRetirementServiceDependencies,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewRetirementService';

/**
 * 转换实体引用 Preview 对象退役服务。
 *
 * 设计说明：
 * 1. 调用方显式提供当前批次拥有的完整 Object3D 清单，本服务不猜测草图对象关系；
 * 2. 对象立即离开场景，避免失效 Preview 继续参与渲染和交互；
 * 3. 自有 geometry 延迟到安全帧边界释放，避免销毁仍被 WebGPU 提交引用的缓冲；
 * 4. Sprite geometry 和所有 material 都可能来自共享工厂，本服务不得释放。
 */
export class SketchEntityReferencePreviewRetirementService {
	/**
	 * 创建 Preview 对象退役服务。
	 *
	 * @param dependencies 安全资源释放调度能力。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferencePreviewRetirementServiceDependencies = {
			scheduleResourceDisposal: scheduleDisposableResourceDisposal,
		},
	) {}

	/**
	 * 退役一个 Preview 批次拥有的全部场景对象。
	 *
	 * @param previewObjects BatchService 已去重收集的 Preview 对象；本方法仍会防御重复输入。
	 */
	public retirePreviewObjects(previewObjects: readonly Object3D[]): void {
		const uniquePreviewObjects = [...new Set(previewObjects)];

		// Preview 标记必须在对象离场时同步清理，避免外部仍持有对象引用时误判其命令状态。
		uniquePreviewObjects.forEach((previewObject) => {
			clearSketchEntityReferencePreview(previewObject);
		});

		// 先摘除整批对象，再安排资源释放，保证后续帧不会重新提交这些对象。
		uniquePreviewObjects.forEach((previewObject) => {
			previewObject.parent?.remove(previewObject);
		});

		this.collectOwnedGeometryResources(uniquePreviewObjects).forEach((geometry) => {
			this.dependencies.scheduleResourceDisposal(geometry);
		});
	}

	/**
	 * 收集当前 Preview 批次真正拥有的 geometry。
	 *
	 * @param previewObjects 已去重的 Preview 对象清单。
	 * @returns 按资源引用去重后的 geometry。
	 */
	private collectOwnedGeometryResources(
		previewObjects: readonly Object3D[],
	): SketchEntityReferencePreviewDisposableResource[] {
		const geometries = new Set<SketchEntityReferencePreviewDisposableResource>();

		previewObjects.forEach((previewObject) => {
			// THREE.Sprite 使用 Three 内部共享 geometry，销毁它会影响其他控制点和标记。
			if (Reflect.get(previewObject, 'isSprite') === true) {
				return;
			}

			const geometry = Reflect.get(previewObject, 'geometry');
			if (
				!geometry
				|| typeof geometry !== 'object'
				|| typeof Reflect.get(geometry, 'dispose') !== 'function'
			) {
				return;
			}

			geometries.add(geometry as SketchEntityReferencePreviewDisposableResource);
		});

		return [...geometries];
	}
}
